import { get_token } from "@/router";
import * as helper from "./event_survey_helper";

export default {
  emits: ['survey-updated', 'survey-deleted'],
  props: {
    event_id: {
      type: [Number, String],
      default: null,
    },
    event: {
      type: Object,
      default: null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return ({
        survey_id: null,
        survey_name: "",
        survey_description: "",
        questions: [],
        filtered_questions: [],
        selected_questions: [],
        created_questions: [],
        show_question_form: false,
        question_types: [],
        errors: {},
        is_edit_mode: false,
        show_event_survey_form: false,
        show_event_survey_answers: false,
        event_survey_answers: {},
        event_survey_errors: [],
        event_survey_first_error_question_id: null,
        event_survey_number_errors: {},
        admin_view_survey: null,
        new_question: {
            text: "",
            question_number: "",
            type: "",
            options: [],
            is_mandatory: false,
        },
      current_event: null,
      current_event_id: null,
    })
  },
  computed: {
    survey_name_len() {
      return String(this.survey_name || "").length;
    },
    survey_description_len() {
      const survey_description = String(this.survey_description || "");
      const plain = survey_description.replace(/<[^>]*>/g, "").trim();
      return plain.length;
    },
    current_survey() {
      return this.resolve_current_survey();
    },
    has_survey() {
      return !!this.current_survey;
    },
    survey_question_total() {
      return Array.isArray(this.current_survey?.survey_questions)
        ? this.current_survey.survey_questions.length
        : 0;
    },
    survey_response_total() {
      return this.count_unique_survey_respondents(this.admin_view_survey || this.current_survey);
    },
  },
  mounted() {
    this.get_question_types();
    this.fetch_questions();
  },
  watch: {
    event: {
      immediate: true,
      deep: false,
      handler(next) {
        if (!this.inline) return;
        if (!next || typeof next !== "object") return;
        this.current_event = next;
        this.current_event_id = next?.id ?? this.event_id;
      },
    },
    event_id: {
      immediate: true,
      handler(next) {
        if (!this.inline) return;
        if (!next) return;
        this.current_event_id = next;
      },
    },
  },
  methods: {
    ...helper,
    resolve_current_survey() {
      const survey = this.get_survey_container();
      return this.normalize_survey_container(survey);
    },
    get_survey_container() {
      return this.current_event?.survey ?? this.event?.survey ?? null;
    },
    normalize_survey_container(survey) {
      if (!survey) return null;
      const first = this.get_first_survey_entry(survey);
      return this.normalize_survey_entry(first);
    },
    get_first_survey_entry(survey) {
      if (!Array.isArray(survey)) return survey;
      if (survey.length === 0) return null;
      return survey[0] || null;
    },
    normalize_survey_entry(entry) {
      if (!entry || typeof entry !== "object") return null;
      const nested = this.get_nested_survey(entry);
      if (!nested) return entry;
      return ({ ...nested, ...entry });
    },
    get_nested_survey(entry) {
      const nested = entry?.survey;
      if (!nested || typeof nested !== "object") return null;
      return nested;
    },
    reset_inline_form() {
      this.is_edit_mode = false;
      this.survey_id = null;
      this.survey_name = "";
      this.survey_description = "";
      this.selected_questions = [];
      this.created_questions = [];
      this.errors = {};
      this.show_question_form = false;
      if (this.$refs.quill_survey_editor_ref) {
        this.$refs.quill_survey_editor_ref.setHTML("");
      }
    },
    clear_new_question() {
      this.new_question = {
        text: "",
        question_number: "",
        type: "",
        options: [],
        is_mandatory: false,
      };
    },
    get_token() {
      return get_token();
    },
    open_create_survey(event) {
      this.is_edit_mode = false;
      this.survey_id = null;
      const defaultName = event?.event_name || event?.name || "";
      const defaultDescription = event?.description || event?.event_description || "";
      this.survey_name = defaultName;
      this.survey_description = defaultDescription;
      this.selected_questions = [];
      this.created_questions = [];
      this.errors = {};
      this.show_question_form = true;
      // track parent event and its id so we can update parent data after actions
      this.current_event = event || null;
      this.current_event_id = event?.id ?? this.event_id ?? null;
      // Survey description is not shown in the UI, but still used in the payload.
      if (this.inline) {
        this.show_event_survey_form = true;
      } else {
        $("#create_survey").modal("show");
      }
    },
    open_edit_survey(survey) {
        this.is_edit_mode = true;
        this.survey_id = survey.id;
        this.survey_name = survey.name;
        this.survey_description = survey.description || "";
        this.selected_questions = (survey.survey_questions || []).map((q) => ({
        id: q.question.id,
        question: q.question.question,
        }));
        this.created_questions = (survey.survey_questions || []).map((q, i) => ({
        id: q.question.id,
        text: q.question.question,
        question_number: i + 1,
        type: q.question.question_type.id,
        options: q.question.options || [],
        is_mandatory: q.question.is_mandatory || false,
        }));
        this.new_question = {
        text: "",
        question_number: "",
        type: "",
        options: [],
        is_mandatory: false,
        };
        this.show_question_form = true;
        this.$nextTick(() => {
        if (this.$refs.quill_survey_editor_ref) {
            this.$refs.quill_survey_editor_ref.setHTML(this.survey_description || "");
        }
        });
        if (this.inline) {
          this.show_event_survey_form = true;
        } else {
          $("#create_survey").modal("show");
        }
    },
    open_view_survey(survey) {
      if (this.inline && this.admin_view_survey?.id === survey?.id) {
        this.admin_view_survey = null;
        return;
      }
      this.admin_view_survey = survey;
      if (this.inline) {
        return;
      }
      $("#view_survey_modal").modal("show");
    },
    close_inline_survey_view() {
      this.admin_view_survey = null;
    },
    count_unique_survey_respondents(survey) {
      const questions = Array.isArray(survey?.survey_questions) ? survey.survey_questions : [];
      const ids = new Set();
      questions.forEach((item) => {
        const answers = Array.isArray(item?.answers) ? item.answers : [];
        answers.forEach((answer) => {
          const userId = answer?.user?.id;
          if (userId !== undefined && userId !== null) {
            ids.add(String(userId));
          }
        });
      });
      return ids.size;
    },
    count_question_respondents(question) {
      return Array.isArray(question?.answers) ? question.answers.length : 0;
    },
    get_question_type_label(questionType) {
      const labels = {
        yes_no: "Yes / No",
        multiple_choice: "Single choice",
        selection: "Dropdown",
        checkbox: "Multi select",
        text: "Written response",
        paragraph: "Long response",
        text_area: "Long response",
        number: "Number",
        date: "Date",
        link: "Link",
      };

      return Reflect.has(labels, questionType)
        ? Reflect.get(labels, questionType)
        : (questionType || "Question");
    },
    on_question_input(event) {
      const input_value = event.target.value.toLowerCase();
      this.filtered_questions = this.questions.filter((q) =>
        q && q.question && q.question.toLowerCase().includes(input_value)
      );
    },
    on_question_select(event) {
      const selected_value = event.target.value;
      const selectedQuestion = this.questions.find(
        (q) => q && q.question && q.question === selected_value
      );

      if (selectedQuestion) {
        this.select_question(selectedQuestion);
      }
    },
    select_question(question) {
      if (!this.selected_questions.some((q) => q.id === question.id)) {
        this.selected_questions.push(question);
      }

      this.new_question.text = question.question;
      this.new_question.type = question.question_type.id;
      this.new_question.options = question.options || [];
      this.new_question.is_mandatory = question.is_mandatory || false;
      this.new_question.question_number = question.question_number || "";

      if (!this.created_questions.some((q) => q.id === question.id)) {
        this.created_questions.push({
          text: question.question,
          id: question.id,
          type: question.question_type.id,
          is_mandatory: question.is_mandatory || false,
        });
      }
      this.filtered_questions = [];

      this.new_question = {
        text: "",
        question_number: "",
        type: "",
        options: [],
        is_mandatory: false,
      };
    },
    validation_for_survey_name() {
      this.errors = {};
      let valid = true;
      if (!this.survey_name || this.survey_name.trim() === "") {
        this.errors.survey_name = "Survey name is required";
        valid = false;
      }
      return valid;
    },
    add_option() {
      if (!Array.isArray(this.new_question.options)) {
        this.new_question.options = [];
      }
      const new_option = { id: Date.now(), text: "" };
      this.new_question.options.push(new_option);
    },
    remove_option(index) {
      this.new_question.options.splice(index, 1);
    },
    remove_created_question(index) {
      const removed = this.created_questions.splice(index, 1)[0];
      this.selected_questions = this.selected_questions.filter((q) => q.id !== removed.id);
    },
    toggle_event_survey_checkbox_answer(question_id, option, isChecked) {
      if (!this.event_survey_answers || typeof this.event_survey_answers !== "object") {
        this.event_survey_answers = {};
      }

      const q_id = String(question_id);
      let current_answers = Reflect.get(this.event_survey_answers, q_id);
      if (!Array.isArray(current_answers)) {
        current_answers = [];
      }

      let updated_answers = [...current_answers];
      if (isChecked) {
        if (!updated_answers.includes(option)) {
          updated_answers.push(option);
        }
      } else {
        updated_answers = updated_answers.filter((a) => a !== option);
      }

      Reflect.set(this.event_survey_answers, q_id, updated_answers);
    },
    validate_event_survey_number(question_id) {
      const value = Reflect.get(this.event_survey_answers, question_id);
      if (value === "" || value === null || value === undefined) {
        Reflect.set(this.event_survey_number_errors, question_id, null);
        return;
      }
      if (isNaN(value)) {
        Reflect.set(this.event_survey_number_errors, question_id, "Please enter a valid number.");
      } else {
        Reflect.set(this.event_survey_number_errors, question_id, null);
      }
    },
  },
}
